<template>
  <div :class="getItemTableContainerClass">
    <ItemTable
      ref="itemTable"
      :additionalBulkActions="getAdditionalBulkActions"
      :boundItemsUrl="boundItemsUrl"
      :canExport="inMainPanel"
      :canModifyColumns="canModifyColumns"
      :class="$style.itemTable"
      :collectionFilters="getCollectionFilters"
      :columnOptions="columnOptions"
      :columns="columns"
      :defaultColumns="defaultColumns"
      :defaultSortField="defaultSortField"
      :details="inMainPanel"
      :disableBoundCount="disableBoundCount"
      :emptyCollectionMessage="emptyCollectionMessage"
      :handleBulkDelete="handleBulkDelete"
      :handleDetailsButtonClick="handleRowClick"
      :handleMainAction="handleMainAction"
      :handleRowClick="handleRowClick"
      :handleUnselectLockedItem="handleUnselectLockedItem"
      :handleUnselectMultipleLockedItems="handleUnselectMultipleLockedItems"
      :hasSelectColumn="hasSelectColumn"
      :inDetailsAsidePanel="!inMainPanel && !inModal"
      :inModal="inModal"
      :isAsideOpen="isAsideOpen"
      :isItemLocked="isItemLocked"
      :itemType="itemType"
      :keepFresh="inMainPanel"
      :onMountedBeforeFetch="handleOnMountedBeforeFetch"
      :parentOS="parentOS"
      :reportType="reportType"
      :route="route"
      :selectedModelId="selectedModelId"
      :selectionItemText="systemItemText"
      :setGettingStartedTooltipVisibility="setGettingStartedTooltipVisibility"
      :setSelectedModelId="setSelectedModelId"
      :showBound="!inMainPanel"
      :showBoundInitially="inUserAside"
      :showBulkAction="false"
      :showBulkDelete="inMainPanel"
      :showMainAction="inMainPanel"
      :showSearchForm="true"
      :showSelectedItemsCount="showSelectedItemsCount"
      :useDropDownFilterComponent="useDropDownFilterComponent"
      :useSearchContextFilterComponent="useSearchContextFilterComponent"
      :viewport="viewport"
      @columnsChange="(columnNames) => $emit('columnsChange', columnNames)"
      @revertColumns="() => $emit('revertColumns')"
    />
    <SystemEnableMFAModal
      ref="enableMFAModal"
      v-model="mfaModal.enableMFAModalVisible"
    />
    <SystemDisableMFAModal
      ref="disableMFAModal"
      v-model="mfaModal.disableMFAModalVisible"
    />
    <ProgressModal
      storeModule="ProgressModule"
    />
  </div>
</template>
<script>
import { ReportTypes } from 'client/Reports/ReportMetadata';
import { events } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import { notify } from 'client/util';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import Constants, {
  DirectoryGroupingConsts,
  ItemTableConsts,
  ItemTypeConsts,
  ItemTypeRoutes,
  ModuleConsts,
  StoreModuleMap,
  osFamilyTypes,
} from 'client/util/Constants';
import EventManager from 'client/GrowthEngineering/EventManager';
import ItemTable from 'components/ItemTable/ItemTable.vue';
import ItemTableComputedMixin from 'components/ItemTable/mixins/ItemTableComputedMixin';
import ItemTableIndirectBindingMixin from 'components/ItemTable/mixins/ItemTableIndirectBindingMixin';
import ItemTableMethodsMixin from 'components/ItemTable/mixins/ItemTableMethodsMixin';
import ItemTablePropsMixin from 'components/ItemTable/mixins/ItemTablePropsMixin';
import ItemType from 'client/util/Constants/ItemType';
import ModalEventBus from 'components/Modal/ModalEventBus';
import ProgressModal from 'components/Modal/ProgressModal';
import RouterService from 'client/util/RouterService';
import SystemDeleteModal from 'components/Modal/SystemDeleteModal.vue';
import SystemDisableMFAModal from 'components/Modal/mfaModals/SystemDisableMFAModal.vue';
import SystemEnableMFAModal from 'components/Modal/mfaModals/SystemEnableMFAModal.vue';
import SystemInsightsDisableModal from 'components/Modal/SystemInsightsDisableModal.vue';
import SystemInsightsEnableModal from 'components/Modal/SystemInsightsEnableModal.vue';
import SystemItemTableColumns from 'client/systems/SystemItemTable/SystemItemTableColumns';
import pluralize from 'pluralize';

const { DeviceItemType } = ItemType;
const deviceItem = new DeviceItemType();

const { error, success } = Constants;

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const systemCollection = StoreModuleMap[ItemTypeConsts.system];

const {
  systemsCouldNotBeDeleted,
} = ModuleConsts.system.errors;

const { mainPanel } = ItemTableConsts.panelType;

const defaultSystemColumns = [
  SystemItemTableColumns.status,
  SystemItemTableColumns.name,
  SystemItemTableColumns.os,
];

export default {
  name: 'SystemItemTable',

  components: {
    ItemTable,
    ProgressModal,
    SystemDisableMFAModal,
    SystemEnableMFAModal,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    ItemTableComputedMixin,
    ItemTableIndirectBindingMixin,
    ItemTableMethodsMixin,
    ItemTablePropsMixin,
  ],

  props: {
    collectionFilters: {
      type: Array,
      default: () => [],
    },
    columnOptions: {
      type: Array,
      default: () => [],
      validator: options => options.every(d => (
        typeof d.name === 'string' && typeof d.value === 'string'
      )),
    },
    columns: {
      type: Array,
      default: () => defaultSystemColumns,
    },
    defaultColumns: {
      type: Array,
      default: () => defaultSystemColumns,
      required: false,
    },
    hasSelectColumn: {
      type: Boolean,
      default: true,
    },
    itemType: {
      type: String,
      default: ItemTypeConsts.device,
    },
    panelType: {
      default: ItemTableConsts.panelType.mainPanel,
      type: String,
    },
    showRecoveryKeyButton: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      groupType: DirectoryGroupingConsts.groupTypes.userGroup,
      memberType: DirectoryGroupingConsts.memberTypes.user,
      mfaModal: {
        disableMFAModalVisible: false,
        enableMFAModalVisible: false,
      },
      reportType: ReportTypes.userToDevice,
      route: ItemTypeRoutes[ItemTypeConsts.deviceList],
      showDevicesFaqExperiment: false,
      showFaqAndDeviceCardsExperiment: false,
      systemItemText: {
        label: this.panelType === mainPanel ? 'selected' : 'bound',
        singular: deviceItem.title,
        plural: deviceItem.titlePlural,
      },
    };
  },

  computed: {
    ...mapGetters(systemCollection, {
      getSudoersToUpdate: 'getSudoersToUpdate',
      selectedItems: 'getSelectedItems',
    }),

    ...mapGetters(settingsModule, {
      hasSystemInsights: 'hasSystemInsights',
    }),

    getAdditionalBulkActions() {
      if (this.inMainPanel) {
        const bulkActionArray = [
          {
            menuItems: [
              {
                label: 'Enable MFA',
                action: this.handleBulkEnableMFA,
              },
              {
                label: 'Disable MFA',
                action: this.handleBulkDisableMFA,
              },
            ],
          },
        ];
        if (this.hasSystemInsights) {
          bulkActionArray[0].menuItems.push({
            label: 'Enable System Insights',
            action: this.handleBulkEnableSystemInsights,
          },
          {
            label: 'Disable System Insights',
            action: this.handleBulkDisableSystemInsights,
          });
        }
        return bulkActionArray;
      }
      return [];
    },

    getCollectionFilters() {
      const collectionFilters = [...this.collectionFilters];

      if ((this.isCommandsAsidePanel()
          || this.isPoliciesAsidePanel()
          || this.isSoftwareAppsAsidePanel())
        && this.parentOS) {
        let filter = {};

        switch (this.parentOS.toLowerCase()) {
          case Constants.windows.toLowerCase():
            filter = { name: 'osFamily', operator: '$regex', value: `/${this.parentOS}/i` };
            break;
          case osFamilyTypes.universal:
            filter = { name: 'osFamily', operator: '$regex', value: `/${osFamilyTypes.windows}|${osFamilyTypes.darwin}|${osFamilyTypes.linux}/i` };
            break;
          default:
            filter = { name: 'osFamily', operator: 'eq', value: this.parentOS };
            break;
        }
        collectionFilters.push(filter);
      }
      return collectionFilters;
    },

    inUserAside() {
      return this.panelType === ItemTableConsts.panelType.userAsidePanel;
    },

    showSelectedItemsCount() {
      return !this.isCommandsAsidePanel()
        && !this.isPoliciesAsidePanel()
        && !this.isSoftwareAppsAsidePanel();
    },

    systemCollection: () => systemCollection,
  },

  beforeCreate() {
    const { parentType } = this.$options.propsData;
    this.$options.methods.handleBeforeCreate(this.$options, systemCollection);

    if (parentType === ItemTypeConsts.systemUser) {
      this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options, true);
    }
  },

  created() {
    const { groupTypes, memberTypes } = DirectoryGroupingConsts;

    // Set the group type for use in ItemTableIndirectBindingMixin
    if (this.isPoliciesAsidePanel() || this.isCommandsAsidePanel()
      || this.isSoftwareAppsAsidePanel()) {
      this.groupType = groupTypes.systemGroup;
      this.memberType = memberTypes.system;
    }
  },

  methods: {
    ...mapActions(systemCollection, [
      'deleteItems',
      'unselectItems',
      'unselectAllItems',
      'updateItems',
      'validateEnableMFASystems',
      'setSortField',
    ]),

    deleteSelectedSystems() {
      ModalEventBus.close();
      const itemsCount = this.selectedItems.length;
      return this.deleteItems(this.selectedItems)
        .then((result) => {
          this.notifyDeleteItems(result, itemsCount);
        })
        .catch(() => {
          notify(error, systemsCouldNotBeDeleted);
        });
    },

    getMemberId(item) {
      if (this.isPoliciesAsidePanel() || this.isCommandsAsidePanel()
      || this.isSoftwareAppsAsidePanel()) {
        return item.id;
      }

      return this.parent._id;
    },

    /* ******************************** DELETE A SYSTEM ******************************** */

    handleBulkDelete() {
      // close aside panel otherwise aside panel for deleted user will be open after delete
      this.closeAsidePanel();
      this.showDeleteModal();
    },

    handleBulkDisableMFA() {
      this.closeAsidePanel();
      this.mfaModal.disableMFAModalVisible = true;
    },

    //* ******************************** Disable System Insights ******************************** */

    handleBulkDisableSystemInsights() {
      // close aside panel otherwise aside panel for disabled system will be open after action
      this.closeAsidePanel();
      const selectedCount = this.selectedItems.length;

      if (!selectedCount) {
        return;
      }

      ModalEventBus.open({
        component: SystemInsightsDisableModal,
      });
    },

    handleBulkEnableMFA() {
      this.closeAsidePanel();
      this.mfaModal.enableMFAModalVisible = true;
    },
    //* ******************************** Enable System Insights ******************************** */

    handleBulkEnableSystemInsights() {
      // close aside panel otherwise aside panel for disabled system will be open after action
      this.closeAsidePanel();
      const selectedCount = this.selectedItems.length;

      if (!selectedCount) {
        return;
      }

      ModalEventBus.open({
        component: SystemInsightsEnableModal,
      });
    },

    handleMainAction() {
      const { triggerEvent } = EventManager;
      triggerEvent(events.addNewSystemButtonClicked);

      RouterService.redirect({
        path: `${ItemTypeRoutes[ItemTypeConsts.device]}/new`,
        viewport: this.viewport,
        vueRouter: this.$router,
      });
    },

    handleOnMountedBeforeFetch() {
      if (this.isCommandsAsidePanel()) {
        this.setSortField({ sortField: '-active' });
      }
    },

    handleRowClick(itemId) {
      if (this.inMainPanel) {
        RouterService.redirect({
          path: `${ItemTypeRoutes[ItemTypeConsts.device]}/${itemId}/details`,
          viewport: this.viewport,
          vueRouter: this.$router,
        });
      }
    },

    isCommandsAsidePanel() {
      return this.panelType === ItemTableConsts.panelType.commandAsidePanel;
    },

    isPoliciesAsidePanel() {
      return this.panelType === ItemTableConsts.panelType.policiesAsidePanel;
    },

    isSoftwareAppsAsidePanel() {
      return this.panelType === ItemTableConsts.panelType.softwareAppsAsidePanel;
    },

    notifyDeleteItems(res, itemsCount) {
      const message = res.deletedIds.length > 0 ? success : error;
      return notify(message, `${res.deletedIds.length} of ${pluralize(deviceItem.title, itemsCount, true)} ${pluralize('has', itemsCount)} been successfully deleted.`);
    },

    showDeleteModal() {
      const selectedCount = this.selectedItems.length;

      if (!selectedCount) {
        return;
      }

      ModalEventBus.open({
        component: SystemDeleteModal,
        componentProps: { action: this.deleteSelectedSystems },
      });
    },
  },
};
</script>
<style module>
.itemTable th:nth-child(3),
.itemTable td:nth-child(3) {
  max-width: 40ch;
}
</style>
